import { type ReactElement } from "react";
import type { TextCardParams } from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";

/**
 * Text card body — Hoshin canvas Yjs widget (PB-234). The card frame + drag
 * header are provided by the adapter (`YjsWidgetBody`); this renders just the
 * body: an always-inline title + note editor (`disabled` when read-only). Plain
 * text only — React escapes text children, so no sanitiser is needed. The body
 * scrolls under the fixed widget size; `min-w-0` + `break-words` keep the
 * responsive-floor gate green.
 */
export function TextCardWidget({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<TextCardParams>): ReactElement {
  return (
    <div className="flex h-full w-full flex-col">
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare title field that takes the card's own type; Input ghost adds a hover wash and an edge, and no approved look row lists the change. */}
      <input
        data-widget-text-field="title"
        value={data.title}
        disabled={readOnly}
        maxLength={255}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
        placeholder="Title"
        aria-label="Text card title"
        className="shrink-0 truncate bg-transparent px-3 pb-1 pt-2.5 text-sm font-semibold text-pb-text rounded-md focus-field placeholder:text-pb-text-muted disabled:cursor-default"
      />
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare body field that fills the card; Textarea ghost adds a hover wash and an edge, and no approved look row lists the change. */}
      <textarea
        data-widget-text-field="body"
        value={data.body}
        disabled={readOnly}
        maxLength={2000}
        onChange={(e) => onChange({ ...data, body: e.target.value })}
        placeholder="Type a note…"
        aria-label="Text card body"
        className="min-h-0 min-w-0 flex-1 resize-none overflow-y-auto whitespace-pre-wrap break-words bg-transparent px-3 pb-3 text-sm leading-5 text-pb-text rounded-md focus-field placeholder:text-pb-text-muted disabled:cursor-default"
      />
    </div>
  );
}
